/**
 * RestroomTapHandler - Explicit handler for Restroom Survey Tags
 * 
 * URL: /standalone/restroom/{TAG_ID}
 * 
 * This component handles restroom facility feedback tags.
 * It calls /api/hotels-standalone/restroom/{tag_id}.
 * 
 * Visual Theme: BLUE (distinguishes from Room Survey which is Green)
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, Droplets, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function RestroomTapHandler() {
  const { tagId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tapData, setTapData] = useState(null);

  useEffect(() => {
    const handleTap = async () => {
      try {
        const url = `${API_URL}/api/hotels-standalone/restroom/${tagId}`;
        
        // Use fetch with redirect: 'manual' to detect 302 vs error
        const response = await fetch(url, {
          method: 'GET',
          redirect: 'manual'
        });
        
        // Status 0 or 'opaqueredirect' type means we got a cross-origin redirect (302)
        // This is SUCCESS - redirect the browser to the API and let it follow
        if (response.status === 0 || response.type === 'opaqueredirect') {
          window.location.href = url;
          return;
        }
        
        // If we got JSON response, check for error or redirect
        if (response.headers.get('content-type')?.includes('application/json')) {
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.detail || 'Restroom tag not found');
          }
          // If we got a success JSON with redirect_url
          if (data.redirect_url) {
            const fullUrl = data.redirect_url.startsWith('http') 
              ? data.redirect_url 
              : `${window.location.origin}${data.redirect_url}`;
            window.location.href = fullUrl;
            return;
          }
          setTapData(data);
        }
        
        // Fallback: redirect to API anyway (let browser handle)
        window.location.href = url;
      } catch (err) {
        console.error('Restroom tap error:', err);
        setError(err.message || 'Failed to load restroom tag');
        setLoading(false);
      }
    };

    if (tagId) {
      handleTap();
    }
  }, [tagId]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Restroom Tag Error</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Tag ID: {tagId}
            </p>
            <p className="text-xs text-gray-400 mt-4">
              If this is a room tag, use /standalone/room/{tagId} instead.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center">
          <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Loading Feedback Form
          </h1>
          <p className="text-gray-600">
            Please wait while we prepare your feedback form...
          </p>
          {tapData && (
            <div className="mt-4 text-sm text-gray-500">
              <p>Location: {tapData.restroom_identifier}</p>
              <Droplets className="w-4 h-4 inline ml-2 text-blue-500" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
